<template>
  <div class="dashboard-wrapper">
    <Sidebar
      :collapsed="sidebarCollapsed"
      :is-tablet="isTablet"
      :mobile-drawer-open="showSidebarMobile"
      @update:collapsed="sidebarCollapsed = $event"
      @close-drawer="showSidebarMobile = false"
    />
    <div
      class="dashboard-main"
      :class="{
        'dashboard-main--collapsed': sidebarCollapsed && !isTablet,
        'dashboard-main--tablet': isTablet,
      }"
      @click="onMainClick"
    >
      <Topbar @toggle-sidebar="showSidebarMobile = !showSidebarMobile" />
      <main class="dashboard-content app-premium-shell" :class="{ 'client-shell': isClientArea, 'staff-shell': !isClientArea }">
        <!-- Offre Duo : onglets Conjoint 1 / Conjoint 2 / Couple uniquement sur le tableau de bord -->
        <div v-if="showDuoDashboardTabs" class="duo-dashboard-tabs" role="tablist" aria-label="Vue tableau de bord duo">
          <button
            type="button"
            role="tab"
            class="duo-dashboard-tab"
            :class="{ active: duoDashboardTab === 'conjoint1' }"
            :aria-selected="duoDashboardTab === 'conjoint1'"
            @click="setDuoDashboardTab('conjoint1')"
          >
            Conjoint 1
          </button>
          <button
            type="button"
            role="tab"
            class="duo-dashboard-tab"
            :class="{ active: duoDashboardTab === 'conjoint2' }"
            :aria-selected="duoDashboardTab === 'conjoint2'"
            @click="setDuoDashboardTab('conjoint2')"
          >
            Conjoint 2
          </button>
          <button
            type="button"
            role="tab"
            class="duo-dashboard-tab"
            :class="{ active: duoDashboardTab === 'couple' }"
            :aria-selected="duoDashboardTab === 'couple'"
            @click="setDuoDashboardTab('couple')"
          >
            Couple
          </button>
        </div>
        <router-view :key="routerViewKey" />
      </main>
    </div>

    <PopCGU
      :open="showCguPopup"
      :required="true"
      :busy="cguSigning"
      :official-url="cguOfficialUrl"
      @accepted="onCguAccepted"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, provide } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { parseDuoDashboardView } from '@/utils/duoDashboardView';
import { getVerifiedRole } from '@/utils/verifiedProfile';
import { MODULE1_CLIENT_OFFER_KEY, MODULE1_CLIENT_FORMULE_KEY } from '@/features/patrimoine/injectionKeys';
import { formuleFromApi, normalizePayableOffer } from '@/utils/paymentPricing';
import Sidebar from '@/components/dashboard/Sidebar.vue';
import Topbar from '@/components/dashboard/Topbar.vue';
import PopCGU from '@/views/patrimoine/client/PopCGU.vue';
import { isAuthenticatedSession } from '@/utils/sessionAuth';
import { flushPendingSignatures } from '@/utils/pendingSignatures';
import { getSessionItem } from '@/utils/storage';

const CGU_TITLE_RE = /cgu|conditions\s+g|condition\s+g[eé]n[eé]rale/i;

export default {
  name: 'DashboardLayout',
  components: {
    Sidebar,
    Topbar,
    PopCGU,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const sidebarCollapsed = ref(false);
    const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);
    const showSidebarMobile = ref(false);
    const showCguPopup = ref(false);
    const cguSigning = ref(false);
    const cguDoc = ref(null);

    const isTablet = computed(() => windowWidth.value >= 777 && windowWidth.value < 1024);
    function updateWidth() {
      windowWidth.value = window.innerWidth;
    }
    function closeSidebar() {
      showSidebarMobile.value = false;
    }

    const clientOffer = ref('persona'); // 'persona' | 'duo' | 'pro'
    const clientFormule = ref('basique'); // 'basique' | 'premium'
    provide(MODULE1_CLIENT_OFFER_KEY, clientOffer);
    provide(MODULE1_CLIENT_FORMULE_KEY, clientFormule);
    const isDashboardRoute = computed(() => {
      const p = route.path || '';
      return p.includes('/analyse-patrimoniale/dashboards');
    });

    const duoDashboardTab = computed(() => parseDuoDashboardView(route.query?.duo_view));

    const showDuoDashboardTabs = computed(() => {
      return isDashboardRoute.value && String(clientOffer.value).toLowerCase() === 'duo';
    });

    // Remonte le router-view à chaque changement de query (dont duo_view) — fullPath inclut la query.
    const routerViewKey = computed(() => route.fullPath || route.path || '');

    const cguOfficialUrl = computed(
      () => cguDoc.value?.contenu_pdf_url || cguDoc.value?.fichier_vierge || '',
    );

    function isLoggedInClient() {
      if (!isAuthenticatedSession()) return false;
      const role = (getVerifiedRole() || '').toLowerCase();
      return role === 'client' || role === 'user';
    }

    /** Habillage crème de l'espace client : les vues back-office gardent le rendu blanc. */
    const isClientArea = ref(false);
    function syncClientArea() {
      isClientArea.value = isLoggedInClient();
    }

    async function loadClientOffer() {
      try {
        if (!isLoggedInClient()) return;
        const { data } = await axios.get('/api/all-clients/', {
          withCredentials: true,
        });
        clientOffer.value = normalizePayableOffer(data?.type_offre || 'persona');
        clientFormule.value = formuleFromApi(data);
      } catch (_) {
        // Silencieux : on n'affiche pas les onglets si on ne peut pas déterminer l'offre.
      }
    }

    function pickCguDocument(docs) {
      const list = Array.isArray(docs) ? docs : [];
      return list.filter((d) => CGU_TITLE_RE.test(String(d?.titre || '')));
    }

    function cguLocalKey() {
      try {
        const uid = sessionStorage.getItem('user_id') || '';
        return `patrimonea_cgu_accepted_${uid || 'anon'}`;
      } catch (_) {
        return 'patrimonea_cgu_accepted_anon';
      }
    }

    function hasLocalCguAcceptance() {
      try {
        return localStorage.getItem(cguLocalKey()) === '1';
      } catch (_) {
        return false;
      }
    }

    function markLocalCguAcceptance() {
      try {
        localStorage.setItem(cguLocalKey(), '1');
      } catch (_) {
        /* ignore */
      }
    }

    async function checkCguAcceptance() {
      try {
        if (!isLoggedInClient()) {
          showCguPopup.value = false;
          cguDoc.value = null;
          return;
        }
        const { data } = await axios.get('/api/documents-a-signer/', {
          withCredentials: true,
        });
        const docs = Array.isArray(data) ? data : data?.documents || data?.results || [];
        const clientApte = Boolean(data?.client_apte);
        const email = String(getSessionItem('email') || '').trim();
        try {
          await flushPendingSignatures({ clientApte, email });
        } catch (_) {
          /* ignore */
        }
        const cguList = pickCguDocument(docs);
        if (cguList.length) {
          const pending = cguList.find((d) => !d.deja_signe) || null;
          cguDoc.value = pending;
          showCguPopup.value = Boolean(pending);
          return;
        }
        // Pas de modèle CGU côté API : popup locale une seule fois par compte.
        cguDoc.value = null;
        showCguPopup.value = !hasLocalCguAcceptance();
      } catch (_) {
        cguDoc.value = null;
        showCguPopup.value = isLoggedInClient() && !hasLocalCguAcceptance();
      }
    }

    async function onCguAccepted(payload) {
      if (cguSigning.value) return;
      const image = payload?.image;
      if (!image) return;

      cguSigning.value = true;
      try {
        const titre = String(cguDoc.value?.titre || '').trim();
        if (titre) {
          await axios.post(
            '/api/templates/sign/',
            {
              titre,
              kind: 'cgu',
              signature_image: image,
              timestamp: payload?.ts || Date.now(),
            },
            { withCredentials: true, timeout: 30000 },
          );
        }
        markLocalCguAcceptance();
        showCguPopup.value = false;
        cguDoc.value = null;
      } catch (_) {
        // Popup reste ouverte pour resigner.
      } finally {
        cguSigning.value = false;
      }
    }

    function setDuoDashboardTab(tab) {
      const t = tab === 'conjoint2' || tab === 'couple' ? tab : 'conjoint1';
      router.replace({
        query: {
          ...route.query,
          duo_view: t,
        },
      });
    }

    function duoViewQueryUnset() {
      const q = route.query?.duo_view;
      if (q === undefined || q === null) return true;
      if (Array.isArray(q)) return q.length === 0 || String(q[0] ?? '').trim() === '';
      return String(q).trim() === '';
    }

    watch(
      [clientOffer, isDashboardRoute, () => route.query?.duo_view],
      () => {
        if (!isDashboardRoute.value) return;
        if (String(clientOffer.value).toLowerCase() !== 'duo') return;
        if (!duoViewQueryUnset()) return;
        router.replace({ query: { ...route.query, duo_view: 'conjoint1' } });
      },
      { flush: 'post' }
    );

    /** Rafraîchir type_offre quand on arrive sur /dashboards depuis une autre page (évite doublon avec onMounted). */
    watch(isDashboardRoute, (on, wasOn) => {
      if (on && !wasOn) loadClientOffer();
    });

    watch(() => route.path, () => {
      syncClientArea();
      checkCguAcceptance();
    });

    onMounted(() => {
      updateWidth();
      window.addEventListener('resize', updateWidth);
      syncClientArea();
      loadClientOffer();
      checkCguAcceptance();
    });
    onUnmounted(() => {
      window.removeEventListener('resize', updateWidth);
    });

    return {
      sidebarCollapsed,
      isTablet,
      isClientArea,
      showSidebarMobile,
      closeSidebar,
      showDuoDashboardTabs,
      duoDashboardTab,
      setDuoDashboardTab,
      routerViewKey,
      showCguPopup,
      cguSigning,
      cguOfficialUrl,
      onCguAccepted,
    };
  },
  methods: {
    onMainClick(ev) {
      if (!this.showSidebarMobile) return;
      // Ne pas fermer si le clic vient du bouton burger (évite open puis close immédiat)
      if (ev.target && (ev.target.closest('.dashboard-topbar__menu-btn') || ev.target.closest('.dashboard-fab'))) return;
      this.closeSidebar();
    },
  },
};
</script>

<style scoped>
.dashboard-wrapper {
  @apply min-h-screen bg-dashboard-bg min-w-safe;
  font-family: var(--font-body);
  position: relative;
}

.dashboard-content :deep(p),
.dashboard-content :deep(.lead),
.dashboard-content :deep(.subtitle),
.dashboard-content :deep(.intro),
.dashboard-content :deep([class*="__lead"]),
.dashboard-content :deep([class*="__intro"]),
.dashboard-content :deep([class*="__description"]),
.dashboard-content :deep([class*="__msg"]),
.dashboard-content :deep(.exec-message),
.dashboard-content :deep(.reco-paragraph) {
  font-family: var(--font-body);
}

/* Mobile: no margin (drawer overlay) */
.dashboard-main {
  @apply min-h-screen transition-all duration-300 ease-out;
  margin-left: 0;
}

/* Tablet: 72px for icon sidebar */
@media (min-width: 640px) {
  .dashboard-main {
    margin-left: 72px;
  }
}

@media (min-width: 1024px) {
  .dashboard-main {
    margin-left: 260px;
  }
  .dashboard-main--collapsed {
    margin-left: 72px;
  }
}

.dashboard-main--tablet {
  /* tablet margin set by media query above */
}

.dashboard-content {
  @apply p-3 sm:p-4 lg:p-6 xl:p-8;
  min-height: calc(100vh - 4rem);
}

/* Onglets Duo – style proche de GestionSignatures.vue (mais local au layout) */
.duo-dashboard-tabs {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0.25rem;
  margin-bottom: 1rem;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
  max-width: 520px;
}

.duo-dashboard-tab {
  flex: 1 1 0;
  border: 0;
  background: transparent;
  color: #334155;
  font-weight: 700;
  padding: 0.55rem 0.75rem;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.duo-dashboard-tab:hover {
  background: rgba(15, 23, 42, 0.04);
}

.duo-dashboard-tab.active {
  background: #0f172a;
  color: #fff;
}

</style>
